import React, { FC, useMemo } from "react";
import Card from "../../Unknown/Card";
import CardContent from "../../Unknown/CardContent";
import CardHeader from "../../Unknown/CardHeader";
import Button from "../../Unknown/Button";
import AppointmentReportGrid from "../AppointmentReportGrid";
import { AppointmentReport } from "../AppointmentReportGrid/types";
import AppointmentReportResendDialog from "../AppointmentReportResendDialog";
import useAppointmentReportResendDialog from "../AppointmentReportResendDialog/useAppointmentReportResendDialog";
import AppointmentReportUnexpectedFindingsDialog from "../AppointmentReportUnexpectedFindingsDialog";
import useAppointmentReportUnexpectedFindingsDialog from "../AppointmentReportUnexpectedFindingsDialog/useAppointmentReportUnexpectedFindingsDialog";
import useAppointmentReportConfirmReceiptDialog from "../AppointmentReportConfirmReceiptDialog/useAppointmentReportConfirmReceiptDialog";
import AppointmentReportConfirmReceiptDialog from "../AppointmentReportConfirmReceiptDialog";
import AppointmentReportAiReportViewDialog from "../AppointmentReportAiReportViewDialog";
import useAppointmentReportAiReportViewDialog from "../AppointmentReportAiReportViewDialog/useAppointmentReportAiReportViewDialog";
import useTranslations from "./useTranslations";
import { getFullName } from "../../../common/getFullName";

type AppointmentAiReport = {
  id: number;
  status: string;
};

export type AppointmentReportGridContainerProps = {
  appointmentId: string;
  patientFirstName: string;
  patientLastName: string;
  patientEmail: string;
  reports: AppointmentReport[];
  appointmentAiReports: AppointmentAiReport[];
  locationCity: string;
  studyNames: string[];
  appointmentDate: string;
  refetch: () => Promise<void>;
};

const AppointmentReportGridContainer: FC<
  AppointmentReportGridContainerProps
> = ({
  appointmentId,
  patientEmail,
  patientFirstName,
  patientLastName,
  reports,
  appointmentAiReports,
  locationCity,
  studyNames,
  appointmentDate,
  refetch,
}) => {
  const { translations } = useTranslations();
  const { title, btnResendReports } = translations;

  const {
    isOpened: isResendDialogOpened,
    onOpenClick: onResendClick,
    props: resendDialogProps,
  } = useAppointmentReportResendDialog({
    appointmentId,
    patientEmail,
    patientFirstName,
    patientLastName,
    refetch,
  });

  const shouldShowResendButton = useMemo(() => {
    return appointmentAiReports.some((report) =>
      ["error", "unsent", "sent"].includes(report.status),
    );
  }, [appointmentAiReports]);

  const {
    isOpened: isUnexpectedFindingsDialogOpened,
    onOpenClick: onUnexpectedFindingsClick,
    props: unexpectedFindingsDialogProps,
  } = useAppointmentReportUnexpectedFindingsDialog({
    reports,
    appointmentId,
    refetch,
  });

  const aiReports = useMemo(() => {
    return reports.filter((report) => report.type === "ai");
  }, [reports]);

  const customerName = useMemo(() => {
    return getFullName({
      firstName: patientFirstName,
      lastName: patientLastName,
    });
  }, [patientFirstName, patientLastName]);

  const {
    isOpened: isConfitmReceiptDialogOpened,
    onOpenClick: onConfirmReceiptClick,
    props: confirmReceiptDialogProps,
  } = useAppointmentReportConfirmReceiptDialog({
    reports,
    appointmentId,
    refetch,
  });

  const {
    isOpened: isAiReportViewDialogOpened,
    onOpenClick: onViewClick,
    props: aiReportViewDialogProps,
  } = useAppointmentReportAiReportViewDialog({
    reports: aiReports,
    customerName,
    locationCity,
    studyNames,
    appointmentDate,
  });

  return (
    <>
      <Card>
        <CardHeader
          title={title}
          action={
            shouldShowResendButton ? (
              <Button variant="contained" onClick={onResendClick}>
                {btnResendReports}
              </Button>
            ) : null
          }
        />
        <CardContent sx={{ pt: 0 }}>
          <AppointmentReportGrid
            reports={reports}
            onConfirmReceiptClick={onConfirmReceiptClick}
            onUnexpectedFindingsClick={onUnexpectedFindingsClick}
            onViewClick={onViewClick}
          />
        </CardContent>
      </Card>
      {resendDialogProps && isResendDialogOpened && (
        <AppointmentReportResendDialog {...resendDialogProps} />
      )}
      {unexpectedFindingsDialogProps && isUnexpectedFindingsDialogOpened && (
        <AppointmentReportUnexpectedFindingsDialog
          {...unexpectedFindingsDialogProps}
        />
      )}
      {confirmReceiptDialogProps && isConfitmReceiptDialogOpened && (
        <AppointmentReportConfirmReceiptDialog {...confirmReceiptDialogProps} />
      )}
      {aiReportViewDialogProps && isAiReportViewDialogOpened && (
        <AppointmentReportAiReportViewDialog {...aiReportViewDialogProps} />
      )}
    </>
  );
};

export default AppointmentReportGridContainer;
